import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import React from 'react';

import AutoPlayVideo from '@/components/media/AutoPlayVideo';

const MEDIA = 'https://media.easy-peasy.ai/landing/video';
const HOME = 'https://media.easy-peasy.ai/landing/home';
const ASSETS = 'https://media.easy-peasy.ai/assets';

type Capability = {
  title: string;
  description: string;
  href: string;
  video: string;
  poster?: string;
  wide?: boolean;
};

const CAPABILITIES: Capability[] = [
  {
    title: 'Text to video',
    description:
      'Describe a scene and get a finished clip — camera moves, physics and sound included.',
    href: '/ai-videos',
    video: `${HOME}/vid-neoncity.mp4`,
    poster: `${HOME}/vid-neoncity-poster.jpg`,
  },
  {
    title: 'Image to video',
    description:
      'Animate any photo or render into lifelike motion that stays true to the original.',
    href: '/ai-animate-image',
    video: `${ASSETS}/motion-control-800-v2.mp4`,
    poster: `${HOME}/motion-control-800-v2-poster.jpg`,
  },
  {
    title: 'Talking avatars',
    description:
      'Give any portrait a voice with studio-grade lip-sync in 40+ languages.',
    href: '/ai-video-generator/talking',
    video: `${ASSETS}/news-anchor.mp4`,
    poster: `${HOME}/news-anchor-poster.jpg`,
  },
  {
    title: 'Or let the Video Agent make the whole thing',
    description:
      'One sentence in — script, scenes, voiceover, music and captions out. Then fine-tune every clip in the editor.',
    href: '/create-video',
    video: `${MEDIA}/filmset.mp4`,
    poster: `${MEDIA}/filmset-poster.jpg`,
    wide: true,
  },
];

const FORMATS: Array<{
  ratio: string;
  label: string;
  video: string;
  poster?: string;
  aspect: string;
}> = [
  {
    ratio: '16:9',
    label: 'YouTube & web',
    video: `${HOME}/vid-coast.mp4`,
    poster: `${HOME}/vid-coast-poster.jpg`,
    aspect: '16/9',
  },
  {
    ratio: '9:16',
    label: 'Reels, TikTok & Shorts',
    video: `${MEDIA}/travel-vertical.mp4`,
    poster: `${MEDIA}/travel-vertical-poster.jpg`,
    aspect: '9/16',
  },
  {
    ratio: '1:1',
    label: 'Feed posts',
    video: `${ASSETS}/talking-vegetables-800.mp4`,
    poster: `${HOME}/talking-vegetables-800-poster.jpg`,
    aspect: '1/1',
  },
];

const VideoCapabilities = () => {
  const { t } = useLocale('ai-videos');

  return (
    <section className='relative py-20 lg:py-28'>
      <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
        <div className='mb-12 text-center'>
          <h2 className='text-4xl font-extrabold tracking-tight text-white md:text-5xl'>
            {t('One prompt away from a finished video')}
          </h2>
          <p className='mx-auto mt-4 max-w-2xl text-lg text-slate-400'>
            {t('Start from text, a photo or a full brief — pick your lane.')}
          </p>
        </div>

        <div className='grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5'>
          {CAPABILITIES.map((capability) => (
            <Link
              key={capability.title}
              href={capability.href}
              className={`group relative block overflow-hidden rounded-3xl border border-white/10 bg-white/5 ${
                capability.wide
                  ? 'md:col-span-3 h-[320px] md:h-[420px]'
                  : 'h-[320px] md:h-[380px]'
              }`}
            >
              <AutoPlayVideo
                src={capability.video}
                poster={capability.poster}
                className='absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]'
              />
              <div className='absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-black/5' />
              <div className='absolute inset-x-0 bottom-0 p-6 md:p-8'>
                <h3 className='text-2xl font-extrabold tracking-tight text-white md:text-3xl'>
                  {t(capability.title)}
                </h3>
                <p className='mt-2 max-w-xl text-sm leading-relaxed text-slate-200 md:text-base'>
                  {t(capability.description)}
                </p>
                <span className='mt-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-semibold text-white backdrop-blur-md transition-all group-hover:gap-3 group-hover:bg-indigo-500'>
                  {t('Try it now')} <span aria-hidden='true'>→</span>
                </span>
              </div>
            </Link>
          ))}
        </div>

        {/* Aspect-ratio trio */}
        <div className='mt-20'>
          <div className='mb-10 text-center'>
            <h3 className='text-3xl font-extrabold tracking-tight text-white md:text-4xl'>
              {t('Every platform, the right frame')}
            </h3>
            <p className='mx-auto mt-3 max-w-xl text-lg text-slate-400'>
              {t('Generate natively in widescreen, vertical or square.')}
            </p>
          </div>
          <div className='flex flex-col items-stretch justify-center gap-4 sm:flex-row sm:items-end'>
            {FORMATS.map((format) => (
              <div key={format.ratio} className='flex flex-col items-center'>
                <div
                  className='relative w-full overflow-hidden rounded-2xl border border-white/10 bg-white/5 sm:h-[320px] sm:w-auto'
                  style={{ aspectRatio: format.aspect }}
                >
                  <AutoPlayVideo
                    src={format.video}
                    poster={format.poster}
                    className='absolute inset-0 h-full w-full object-cover'
                  />
                  <span className='absolute left-3 top-3 rounded-full bg-black/55 px-3 py-1 text-xs font-bold text-white backdrop-blur-md'>
                    {format.ratio}
                  </span>
                </div>
                <div className='mt-3 text-sm font-semibold text-slate-300'>
                  {t(format.label)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default VideoCapabilities;
